import Raven from 'raven-js';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { Button } from 'foundations-components/transitional/components/Button';
import { LoadingButton } from 'foundations-components/transitional/components/LoadingButton';
import { useEffect, useMemo, useState } from 'react';
import { usePurchaseMotionForAssignability } from '../../../hooks/usePurchaseMotionForAssignability';
import { potentiallyOverridenAssignability } from '../../../utils/talkToSales';
import { trackPotentialBannerInteraction } from '../../../utils/ums';
import { getTrackingProperties, track } from '../../../utils/usageTracker';
import { ErrorBoundary, ErrorBoundaryNames } from '../../ErrorBoundary';
import { PricingPageRedirectButton } from '../PricingPageRedirectButton/PricingPageRedirectButton';
import CommModalContainer from './CommModalContainer';
import { isRpcError } from '../../../utils/error';
import { trackPurchaseMotionFailure } from '../../../utils/metrics';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const TalkToSalesButtonBase = props => {
  const {
    apiName,
    children,
    isAssignable: originalAssignability,
    level,
    location,
    trackingData,
    upgradeConfig,
    uiButtonProps,
    onClick,
    'data-test-id': dataTestId,
    'data-selenium-id': dataSeleniumId
  } = props;
  const [showModal, setShowModal] = useState(false);
  const isAssignable = useMemo(() => potentiallyOverridenAssignability(originalAssignability), [originalAssignability]);
  const trackingProperties = useMemo(() => getTrackingProperties({
    apiName,
    trackingData,
    upgradeConfig
  }), [apiName, trackingData, upgradeConfig]);
  const {
    error,
    loading,
    purchaseMotion
  } = usePurchaseMotionForAssignability({
    isAssignable,
    apiName
  });
  const handleClick = () => {
    onClick === null || onClick === void 0 || onClick('TALK_TO_SALES');
    track('contactSalesButtonInteraction', Object.assign({
      action: 'clicked'
    }, trackingProperties, {
      isAssignable: purchaseMotion === 'TALK_TO_SALES'
    }));
    if (upgradeConfig) {
      track('interaction', Object.assign({
        action: 'clicked upgrade point',
        isAssignable: purchaseMotion === 'TALK_TO_SALES'
      }, trackingProperties));
      track('interaction', Object.assign({
        action: 'clicked talk to sales',
        isAssignable: purchaseMotion === 'TALK_TO_SALES',
        isPrimaryCta: level === 0,
        isSecondaryCta: level === 1
      }, trackingProperties));
      trackPotentialBannerInteraction(upgradeConfig).catch(e => {
        if (!isRpcError(e) || e.cause.status !== 0) {
          Raven.captureException(e, {
            fingerprint: ['Failed to submit interaction from TalkToSalesButton'],
            extra: {
              upgradeConfig
            }
          });
        }
      });
    }
    setShowModal(true);
  };
  const handleClose = () => {
    setShowModal(false);
  };
  useEffect(() => {
    if (!loading) {
      track('contactSalesButtonInteraction', Object.assign({
        action: 'viewed'
      }, trackingProperties));
      if (upgradeConfig) {
        track('interaction', Object.assign({
          action: 'viewed talk to sales',
          isAssignable: purchaseMotion === 'TALK_TO_SALES',
          isPrimaryCta: level === 0,
          isSecondaryCta: level === 1
        }, trackingProperties));
      }
    }
  }, [loading, level, location, purchaseMotion, trackingProperties, upgradeConfig, isAssignable]);
  useEffect(() => {
    if (error) {
      trackPurchaseMotionFailure({
        uniqueId: trackingData.uniqueId,
        apiName,
        purchaseMotion: 'TALK_TO_SALES'
      });
      Raven.captureException(error, {
        fingerprint: ['Failed to load TALK_TO_SALES_BUTTON purchase motion'],
        extra: {
          upgradeConfig
        }
      });
    }
  }, [apiName, error, trackingData.uniqueId, upgradeConfig]);
  if (purchaseMotion === 'PRICING_PAGE_REDIRECT') {
    // A pp redirect motion will only be returned for users in Russia
    return /*#__PURE__*/_jsx(PricingPageRedirectButton, Object.assign({}, props));
  }
  if (loading) {
    return /*#__PURE__*/_jsx(LoadingButton, Object.assign({}, uiButtonProps, {
      loading: loading,
      children: children !== null && children !== void 0 ? children : /*#__PURE__*/_jsx(FormattedMessage, {
        message: "purchaseMotions.buttons.talkToSales"
      })
    }));
  }
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [showModal && /*#__PURE__*/_jsx(CommModalContainer, {
      apiName: apiName,
      trackingData: trackingData,
      isAssignable: purchaseMotion === 'TALK_TO_SALES',
      onClose: handleClose
    }), /*#__PURE__*/_jsx(Button, Object.assign({
      truncate: true,
      truncateStringProps: {
        justifyContent: 'center'
      }
    }, uiButtonProps, {
      onClick: handleClick,
      "data-test-id": dataTestId,
      "data-selenium-id": dataSeleniumId,
      children: children !== null && children !== void 0 ? children : /*#__PURE__*/_jsx(FormattedMessage, {
        message: "purchaseMotions.buttons.talkToSales"
      })
    }))]
  });
};
export const TalkToSalesButton = props => /*#__PURE__*/_jsx(ErrorBoundary, {
  boundaryName: ErrorBoundaryNames.TALK_TO_SALES_BUTTON,
  children: /*#__PURE__*/_jsx(TalkToSalesButtonBase, Object.assign({}, props))
});